/**
 * Tenant Context Utility
 *
 * Extracts and manages tenant information from the subdomain
 * Works with Cloudflare Worker wildcard routing (*.atoscloud.app)
 */

export interface TenantInfo {
  subdomain: string | null;
  isTenantContext: boolean;
  isMainApp: boolean;
}

/**
 * Extracts tenant subdomain from current hostname
 *
 * Examples:
 * - abc.atoscloud.app → "abc"
 * - app.atoscloud.app → null (main app)
 * - abc.localhost → "abc" (development with subdomain)
 * - localhost → null (development, no tenant)
 */
export const getTenantSubdomain = (): string | null => {
  const hostname = window.location.hostname.toLowerCase();
  const parts = hostname.split('.');

  // Development environment with .localhost subdomain (e.g., tenant.localhost)
  if (parts.length >= 2 && parts[parts.length - 1] === 'localhost') {
    const subdomain = parts[0];

    // Exclude common subdomains
    if (subdomain === 'www' || subdomain === 'api' || subdomain === 'admin') {
      return null;
    }

    // Validate subdomain format (lowercase, alphanumeric, hyphens only)
    if (!/^[a-z0-9-]+$/.test(subdomain)) {
      console.warn(`Invalid subdomain format: ${subdomain}`);
      return null;
    }

    console.log(`🏢 Development mode: Using subdomain "${subdomain}" from ${hostname}`);
    return subdomain;
  }

  // Plain localhost without subdomain (e.g., localhost or 127.0.0.1)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Fallback: Check for tenant override in localStorage for legacy testing
    const devTenant = localStorage.getItem('dev_tenant_subdomain');
    if (devTenant) {
      console.log(`🏢 Development mode: Using subdomain "${devTenant}" from localStorage (legacy)`);
      console.warn('⚠️ Consider using tenant.localhost instead of localStorage for testing');
    }
    return devTenant;
  }

  // Production environment (*.atoscloud.app)
  // Check if it's a subdomain (e.g., abc.atoscloud.app)
  if (parts.length === 3 && parts[1] === 'atoscloud' && parts[2] === 'app') {
    const subdomain = parts[0];

    // Exclude main app domains
    if (subdomain === 'app' || subdomain === 'www') {
      return null;
    }

    // Validate subdomain format (lowercase, alphanumeric, hyphens only)
    if (!/^[a-z0-9-]+$/.test(subdomain)) {
      console.warn(`Invalid subdomain format: ${subdomain}`);
      return null;
    }

    return subdomain;
  }

  return null;
};

/**
 * Gets full tenant information
 */
export const getTenantInfo = (): TenantInfo => {
  const subdomain = getTenantSubdomain();

  return {
    subdomain,
    isTenantContext: subdomain !== null,
    isMainApp: subdomain === null
  };
};

/**
 * Checks if current context is a tenant (not main app)
 */
export const isTenantContext = (): boolean => {
  return getTenantSubdomain() !== null;
};

/**
 * Checks if current context is main app
 */
export const isMainApp = (): boolean => {
  return getTenantSubdomain() === null;
};

/**
 * Sets tenant subdomain for local development
 * Use this in development to simulate tenant context
 *
 * Example:
 * setDevTenantSubdomain('test-tenant')
 */
export const setDevTenantSubdomain = (subdomain: string | null): void => {
  if (subdomain === null) {
    localStorage.removeItem('dev_tenant_subdomain');
  } else {
    localStorage.setItem('dev_tenant_subdomain', subdomain);
  }

  console.log(`Development tenant subdomain set to: ${subdomain}`);
  console.log('Reload the page to apply changes.');
};

/**
 * Redirects to tenant subdomain
 *
 * Examples:
 * - Production: redirectToTenantSubdomain('abc') → https://abc.atoscloud.app
 * - Development: redirectToTenantSubdomain('abc') → http://abc.localhost:3000
 */
export const redirectToTenantSubdomain = (subdomain: string, path: string = '/'): void => {
  const hostname = window.location.hostname.toLowerCase();
  const protocol = window.location.protocol;
  const port = window.location.port;

  // In development, redirect to subdomain.localhost:port
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    const portSuffix = port ? `:${port}` : '';
    const tenantUrl = `${protocol}//${subdomain}.localhost${portSuffix}${path}`;
    window.location.href = tenantUrl;
    return;
  }

  // Production: redirect to tenant subdomain
  const tenantUrl = `${protocol}//${subdomain}.atoscloud.app${path}`;
  window.location.href = tenantUrl;
};

/**
 * Redirects to main app domain
 *
 * Examples:
 * - Production: redirectToMainApp('/login') → https://app.atoscloud.app/login
 * - Development: redirectToMainApp('/login') → http://localhost:3000/login
 */
export const redirectToMainApp = (path: string = '/'): void => {
  const hostname = window.location.hostname.toLowerCase();
  const protocol = window.location.protocol;
  const port = window.location.port;

  // In development, redirect to plain localhost (no subdomain)
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    const portSuffix = port ? `:${port}` : '';
    const mainAppUrl = `${protocol}//localhost${portSuffix}${path}`;
    window.location.href = mainAppUrl;
    return;
  }

  // Production: redirect to main app
  const mainAppUrl = `${protocol}//app.atoscloud.app${path}`;
  window.location.href = mainAppUrl;
};

/**
 * Gets the appropriate base URL for the current context
 * Used for generating absolute URLs
 */
export const getBaseUrl = (): string => {
  const tenantInfo = getTenantInfo();
  const protocol = window.location.protocol;
  const hostname = window.location.hostname.toLowerCase();
  const port = window.location.port;

  // Development (including .localhost subdomains)
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    const portSuffix = port ? `:${port}` : '';

    // If tenant context, use subdomain.localhost
    if (tenantInfo.isTenantContext && tenantInfo.subdomain) {
      return `${protocol}//${tenantInfo.subdomain}.localhost${portSuffix}`;
    }

    // Otherwise plain localhost
    return `${protocol}//localhost${portSuffix}`;
  }

  // Production with tenant
  if (tenantInfo.isTenantContext) {
    return `${protocol}//${tenantInfo.subdomain}.atoscloud.app`;
  }

  // Production main app
  return `${protocol}//app.atoscloud.app`;
};

/**
 * Logs current tenant context (for debugging)
 */
export const logTenantContext = (): void => {
  const info = getTenantInfo();

  console.group('🏢 Tenant Context');
  console.log('Subdomain:', info.subdomain || '(none)');
  console.log('Is Tenant Context:', info.isTenantContext);
  console.log('Is Main App:', info.isMainApp);
  console.log('Base URL:', getBaseUrl());
  console.log('Hostname:', window.location.hostname);
  console.groupEnd();
};

// Log tenant context on module load (only in development)
if (process.env.NODE_ENV === 'development') {
  logTenantContext();
}
